// Shopping Bid Strategy — Bidding section, deterministic check.
// Per-campaign bid strategy + current ROAS/CPA target, non-value-based (the review items) first.

import classNames from 'classnames';

import type { EvidenceProps } from './registry';

interface CampaignRow {
  id?: string;
  name: string;
  strategyLabel: string;
  targetLabel: string;
  isVbb: boolean;
}

interface BidStrategyShape {
  totalCampaigns: number;
  valueBasedCount: number;
  volumeBasedCount: number;
  campaigns: CampaignRow[];
}

export const ShoppingBidStrategyEvidence = ({ evidence }: EvidenceProps) => {
  const e = evidence as unknown as BidStrategyShape;

  return (
    <div className='evidence'>
      <section className='evidence__section'>
        <h4 className='evidence__section-title'>
          Bid strategy mix
          <span className='evidence__section-tag evidence__section-tag--context'>
            {e.totalCampaigns} Shopping campaigns
          </span>
        </h4>
        <div className='audience-coverage'>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{e.valueBasedCount.toLocaleString()}</div>
            <div className='audience-coverage__lbl'>Value-based</div>
          </div>
          <div className='audience-coverage__cell'>
            <div className='audience-coverage__num'>{e.volumeBasedCount.toLocaleString()}</div>
            <div className='audience-coverage__lbl'>Legacy / volume-based</div>
          </div>
        </div>
      </section>

      <section className='evidence__section'>
        <h4 className='evidence__section-title'>Per-campaign strategy &amp; target</h4>
        <table className='evidence__table evidence__table--pmax-bidding'>
          <thead>
            <tr>
              <th>Campaign</th>
              <th>Bid strategy</th>
              <th>Target</th>
            </tr>
          </thead>
          <tbody>
            {e.campaigns.map(c => (
              <tr
                key={c.id ?? c.name}
                className={classNames({ 'evidence__row--flagged': !c.isVbb })}
              >
                <td>{c.name}</td>
                <td>
                  <span
                    className={`evidence__strength ${
                      c.isVbb ? 'evidence__strength--good' : 'evidence__strength--average'
                    }`}
                  >
                    {c.strategyLabel}
                  </span>
                </td>
                <td>{c.targetLabel}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <p className='evidence__footnote'>
        Tinuiti prefers value-based bidding (Maximize Conversion Value or Target ROAS) wherever
        first-party value data supports it — it optimizes for total revenue rather than clicks.
        Legacy and volume strategies (Manual / Enhanced CPC, Maximize Clicks) can be intentional for
        some cases, so they're flagged for review rather than treated as errors.
      </p>
    </div>
  );
};
